"use client";

import _ from "lodash";
import { useSession } from "next-auth/react";
import { useEffect } from "react";
import { useProfilesStore, useScheduleStore } from "@/app/schedules/[tn]/CombinedStore";
import { parseProfile, useCreateProfileWithVoiceSettings, useProfilesQuery, useScheduleQuery, useUpdateSchedule } from "@/app/schedules/[tn]/utils";
import {
    AutoAttendantStatusEnum,
    CallForwardingVariableStatusEnum,
    SelectiveCallForwardingStatusEnum,
    SequentialRingStatusEnum,
    SimultaneousRingStatusEnum,
} from "@/clients/ntas/basic-selfcare";
import { PredefinedSettingsSchedule } from "@/clients/ntas/profiles";
import { checkStoredBusinessAttendantCapability, checkStoredScheduleCapability } from "@/stores/CapabilitiesStore";
import useTNStore from "@/stores/TNStore";
import { formatPuid } from "@/utils/general";
import { LoggerObject } from "@/utils/logger";

export default function CheckDefaultProfile() {
    const { status } = useSession();
    const hasScheduleCapability = checkStoredScheduleCapability();
    if (!hasScheduleCapability) return <></>;

    if (process.env.NEXT_PUBLIC_AUTH_ON?.toLocaleLowerCase() === "true") {
        if (status === "authenticated") return <CreateDefaultProfile />;
        return <></>;
    } else {
        return <CreateDefaultProfile />;
    }
}

// Created this file becuase for calling api queries we need the component to be at root level and wrapped in QueryClientProvider
function CreateDefaultProfile() {
    const { tn } = useTNStore();
    const { schedule, setSchedule } = useScheduleStore();
    const { profiles, setProfiles, setProfilesMap } = useProfilesStore();
    const hasBusinessAttendant = checkStoredBusinessAttendantCapability();

    const { isPending: pendingSchedule, data: scheduleData } = useScheduleQuery(tn);
    const { isPending: profilesPending, data: profilesData } = useProfilesQuery(tn);
    const { isPending: profileCreatePending, isError: createProfileFailed, mutate: createProfile } = useCreateProfileWithVoiceSettings(tn);
    const { mutateAsync: updateSchedule, isPending: scheduleUpdatePending } = useUpdateSchedule(tn);

    useEffect(() => {
        if (scheduleData && !_.isEqual(schedule, scheduleData)) {
            setSchedule(scheduleData.predefinedSettingsSchedule);
        }
    }, [scheduleData, schedule, setSchedule]);

    useEffect(() => {
        if (profilesData && profilesData.predefinedSettingsIdList.profile && !_.isEqual(profiles, profilesData.predefinedSettingsIdList.profile)) {
            setProfiles(profilesData.predefinedSettingsIdList.profile);
            setProfilesMap(parseProfile(profilesData.predefinedSettingsIdList.profile));
        }
    }, [profilesData, profiles]);

    useEffect(() => {
        const defaultProfileName = "Default Active Schedule";
        const defaultProfile =
            profilesData?.predefinedSettingsIdList?.profile?.length &&
            profilesData?.predefinedSettingsIdList?.profile?.find((prf) => prf.profileName === defaultProfileName);
        if (!profilesPending && !pendingSchedule && !profilesData?.predefinedSettingsIdList?.profile?.length && !profileCreatePending && !createProfileFailed) {
            // only checking for profiles length, because there cannot be a schedule without corresponding profile
            const loggerObj = new LoggerObject("createDefaultProfileHandler", undefined);
            loggerObj.getLogger().debug("Entered Function");

            const tmpProfile = {
                predefinedSettingsProfile: {
                    profileName: defaultProfileName,
                    profileFeatureList: {
                        featureList: [
                            {
                                puid: formatPuid(tn),
                                communicationSettings: {
                                    callForwardingVariable: {
                                        status: CallForwardingVariableStatusEnum.Off,
                                    },
                                    selectiveCallForwarding: {
                                        status: SelectiveCallForwardingStatusEnum.Off,
                                    },
                                    simultaneousRing: {
                                        status: SimultaneousRingStatusEnum.Off,
                                    },
                                    sequentialRing: {
                                        status: SequentialRingStatusEnum.Off,
                                    },
                                },
                            },
                        ],
                    },
                },
            };
            if (hasBusinessAttendant) {
                const path: string = "predefinedSettingsProfile.profileFeatureList.featureList[0].communicationSettings.autoAttendant";
                _.setWith(tmpProfile, path, { status: AutoAttendantStatusEnum.Off }, _.cloneDeep);
            }
            createProfile({
                newProfile: tmpProfile,
                parentLoggerObj: loggerObj,
            });
        } else if (
            !profilesPending &&
            !pendingSchedule &&
            !profileCreatePending &&
            !createProfileFailed &&
            !scheduleUpdatePending &&
            defaultProfile &&
            scheduleData &&
            (!scheduleData.predefinedSettingsSchedule.defaultProfileId || scheduleData.predefinedSettingsSchedule.defaultProfileId !== defaultProfile.profileId)
        ) {
            const tmpSchedule: PredefinedSettingsSchedule = _.cloneDeep(scheduleData.predefinedSettingsSchedule);
            tmpSchedule.defaultProfileId = defaultProfile?.profileId;
            tmpSchedule.removeExpiredEntries = false;
            tmpSchedule.isActive = false;
            if (!_.isEqual(scheduleData.predefinedSettingsSchedule, tmpSchedule)) {
                updateSchedule({ predefinedSettingsSchedule: tmpSchedule });
            }
        }
    }, [profilesPending, profilesData, profileCreatePending, pendingSchedule, scheduleData, createProfileFailed, scheduleUpdatePending]);

    return <></>;
}
